(function () {
  "use strict";

  /**
   * This controls the cart attachments view
   *
   * General idea is to modify the state (attachments/messages) then simply re-render
   * everything on each change. (only render() touches the DOM)
   */
  class CartAttachments {
    constructor(element, attachedData, options) {
      this.element = element;
      this.options = options;

      this.attachments = {};
      this.attachmentsState = {};
      this.messages = [];

      this.attachedData = attachedData;

      // configuration for each attachment name
      // only the names that have a config will be supported
      this.options.attachmentsConfig = {};
      this.options.attachmentsConfig[
        this.options.constants.AttachmentName.STATEMENT_OF_WORK
      ] = {
        mandatory: true,
        confirmEmpty: false,
        defaultUrl: this.options.urls.default_statement_of_work,
      };
      // init state for all supported attachment names (i.e present in attachmentsConfig)
      for (const name of Object.keys(this.options.attachmentsConfig)) {
        this.attachmentsState[name] = {
          confirmedEmpty: false,
          useDefault: false,
        };
      }

      // attach to DOM
      this.attachDOM(this.element);

      // initial load and render
      this.getAttachments().then(() => {
        if (!User.isStaff) {
          this.uploadAttachmentByUrl(
            this.options.constants.AttachmentName.STATEMENT_OF_WORK,
            this.options.urls.default_statement_of_work
          ).then(() => {
            this.render();
          });
        } else this.render();
      });

      // Ensure attachedData and attachments are defined
      this.attachedData.getData().then(() => {
        if (this.attachedData.data && this.attachedData.data.attachments) {
          for (const name of Object.keys(this.options.attachmentsConfig)) {
            if (name in this.attachedData.data.attachments) {
              this.attachmentsState[name].confirmedEmpty =
                this.attachedData.data.attachments[name].confirmedEmpty;
            }
          }
        }
        this.render();
      });
    }

    /**
     * Attach the controller to the DOM, setup events
     * @param {HTMLElement} element - The DOM element
     * @returns {void}
     */
    attachDOM(element) {
      this.element = element;
      this.attachmentsContainer = this.element.querySelector(
        "#attachments-container"
      );
      this.uploadForm = this.element.querySelector("#attachment-upload-form");
      this.nameSelect = this.uploadForm.querySelector(
        "#attachment-name-select"
      );
      this.fileInput = this.uploadForm.querySelector("input[type='file']");
      this.uploadButton = this.uploadForm.querySelector(
        "#attachment-upload-button"
      );
      this.messagesContainer = this.element.querySelector(
        "#attachments-messages"
      );

      if (!User.isStaff) {
        this.statementCheckbox =
          this.element.querySelector("#statement-of-work");
        this.personalizedStatement = this.element.querySelector(
          "#personalized-statement"
        );
        this.statementCheckbox.checked = true;
        if (!this.personalizedStatement.classList.contains("hidden")) {
          this.personalizedStatement.classList.add("hidden");
        }
      }

      // events
      this.fileInput.addEventListener("change", (event) => {
        this.uploadAttachment().then(() => {
          this.getAttachments().then(() => {
            this.validate();
          });
        });
      });

      this.uploadButton.addEventListener("click", (event) => {
        this.fileInput.click();
      });
      if (!User.isStaff) {
        this.statementCheckbox.addEventListener("click", (event) => {
          event.preventDefault();
          this.personalizedStatement.classList.toggle("hidden");
        });
      }
    }

    /**
     * Attach the controller to a new DOM element and re-render the current state
     * @param {HTMLElement} element - The new DOM element
     * @returns {void}
     *
     */
    reset(element) {
      this.attachDOM(element);
      this.render();
    }

    /**
     * Fetch the attachments from the server
     * @returns {Promise} A promise that resolves when the attachments are fetched
     */
    getAttachments() {
      return fetch(this.options.urls.satchmo_cart_attachments)
        .then((response) => response.json())
        .then((data) => {
          this.attachments = data;
        })
        .catch((error) => {
          console.log(error);
          this.message(
            "error",
            gettext("Unexpected error. Please try again or contact support.")
          );
        });
    }

    /**
     * Display a single message (render immediately)
     * @param {string} type - The type of message (error, warning, info, success)
     * @param {string} message - The message to display
     * @returns {void}
     */
    message(type, message) {
      this.messages = [
        {
          type: type,
          message: message,
        },
      ];
      this.render();
    }

    /**
     * Translate a text
     * @param {string} text - The text to translate
     * @returns {string} The translated text
     */
    translate(text) {
      return this.options.translations[text] || text;
    }

    /**
     * Render the attachments and messages (clears everything before)
     * @returns {void}
     */
    render() {
      // clear the file input otherwise the same filename won't trigger the change event
      this.fileInput.value = "";

      // clear the attachments container
      while (this.attachmentsContainer.firstChild) {
        this.attachmentsContainer.removeChild(
          this.attachmentsContainer.lastChild
        );
      }

      // render attachment rows
      for (const [name, config] of Object.entries(
        this.options.attachmentsConfig
      )) {
        const attachment = this.attachments.hasOwnProperty(name)
          ? this.attachments[name]
          : null;
        const state = this.attachmentsState.hasOwnProperty(name)
          ? this.attachmentsState[name]
          : null;

        if (!User.isStaff) {
          //uploading the state of work by default in template
          const defaultAttachment = {
            confirmedEmpty: false,
            useDefault: true,
            url: config.defaultUrl,
          };

          let template = document.createElement("template");
          template.innerHTML = `<div class="cart-attachment">
                  <h2 class="heading-secondary-medium">${this.translate(
                    name
                  )}:</h2>
                  <div class="attachment-row">
                      <a href="${defaultAttachment.url}" target="_blank">
                          Sculpteo's medical statement of work
                      </a>
                  </div>
              </div>`;
          this.attachmentsContainer.appendChild(template.content.firstChild);
        } else {
          let attachmentRow = "";
          if (attachment) {
            attachmentRow = `
              <a href="${attachment.url}" target="_blank">
                ${attachment.filename}
              </a>
              <button class="btn btn-default btn-sm attachment-action-button" data-action="delete">
                ${gettext("Delete")}
              </button>
            `;
          } else {
            if (!state.confirmedEmpty) {
              const tr1 = gettext("Upload");
              attachmentRow = `
                <button class="btn btn-default btn-sm attachment-action-button" data-action="upload">
                  <span class="attachment-upload-icon icon-upload"></span>
                  ${tr1}
                </button>
                <span>${gettext("or")}</span>
              `;
            }
            if (config.defaultUrl) {
              const tr2 = gettext("Use default");
              attachmentRow += `
                <button class="btn btn-default btn-sm attachment-action-button" data-action="use-default">
                  ${tr2}
                </button>
              `;
            }
          }

          let template = document.createElement("template");
          template.innerHTML = `<div class="cart-attachment">
              <h2 class="heading-secondary-medium">${this.translate(name)}:</h2>
              <div class="attachment-row">
                ${attachmentRow}
              </div>
            </div>`;

          let attachmentElement = this.attachmentsContainer.appendChild(
            template.content.firstChild
          );

          // bind actions
          let uploadButton = attachmentElement.querySelector(
            "button[data-action='upload']"
          );
          if (uploadButton) {
            uploadButton.addEventListener("click", () => {
              this.attachmentsState[name] = {
                confirmedEmpty: false,
                useDefault: false,
              };
              this.nameSelect.value = name;
              this.fileInput.click();
            });
          }

          let useDefaultButton = attachmentElement.querySelector(
            "button[data-action='use-default']"
          );
          if (useDefaultButton) {
            useDefaultButton.addEventListener("click", () => {
              this.attachmentsState[name] = {
                confirmedEmpty: false,
                useDefault: true,
              };
              this.uploadAttachmentByUrl(name, config.defaultUrl).then(() => {
                this.getAttachments().then(() => {
                  this.render();
                });
              });
            });
          }

          let deleteButton = attachmentElement.querySelector(
            "button[data-action='delete']"
          );
          if (deleteButton) {
            deleteButton.addEventListener("click", () => {
              this.deleteAttachment(name).then(() => {
                this.getAttachments().then(() => {
                  this.render();
                });
              });
            });
          }
        }
      }
      this.messagesContainer.innerHTML = "";
      for (const message of this.messages) {
        let template = document.createElement("template");
        template.innerHTML = `<div class="alert alert-${message.type}">${message.message}</div>`;
        this.messagesContainer.appendChild(template.content.firstChild);
      }
    }

    /**

     * Check if the cart attachments are valid and clears or sets the messages accordingly
     * @returns {boolean} True if the cart attachments are valid, false otherwise
     */
    validate() {
      let isValid = true;

      // check that all mandatory attachments are present
      if (User.isStaff) {
        for (const [name, config] of Object.entries(
          this.options.attachmentsConfig
        )) {
          const attachment = this.attachments.hasOwnProperty(name)
            ? this.attachments[name]
            : null;
          const state = this.attachmentsState.hasOwnProperty(name)
            ? this.attachmentsState[name]
            : null;
          if (config.mandatory && !attachment && !state.useDefault) {
            isValid = false;
            this.message(
              "error",
              String.format(
                gettext("Please provide a '{0}' document!"),
                this.translate(name)
              )
            );
          }
        }
      }

      if (isValid) {
        this.messages = [];
        this.render();
      }

      return isValid;
    }

    /**
     * Delete an attachment
     * @param {string} name - The name of the attachment to delete
     * @returns {Promise} A promise that resolves when the attachment is deleted
     */
    deleteAttachment(name) {
      // note that we don't init the FormData with the form element, we copy the csrf token
      // value from the form element to the header; with django PUT/DELETE requests we
      // need to set the csrf token in the header. c.f below
      const formData = new FormData();
      formData.set("name", name);

      return fetch(this.options.urls.satchmo_cart_attachments, {
        method: "DELETE",
        body: formData,
        headers: {
          "x-csrftoken": this.uploadForm.csrfmiddlewaretoken.value,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data.success) {
            throw new Error(
              `An error occured when deleting the attachment: ${data.errors}`
            );
          }
        })
        .catch((error) => {
          console.log(error);
          this.message(
            "error",
            gettext("Unexpected error. Please try again or contact support.")
          );
        });
    }

    uploadAttachmentByUrl(name, url) {
      // download the file from the url then post it
      return fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const fileName = url.split("/").pop();
          const formData = new FormData();
          formData.set("name", name);
          formData.set("attachment", blob, fileName);
          formData.set(
            "csrfmiddlewaretoken",
            this.uploadForm.csrfmiddlewaretoken.value
          );
          return fetch(this.options.urls.satchmo_cart_attachments, {
            method: "POST",
            body: formData,
          });
        })
        .then((response) => response.json())
        .then((data) => {
          if (!data.success) {
            throw new Error(
              `An error occured when uploading the attachment: ${data.errors}`
            );
          }
        })
        .catch((error) => {
          console.log(error);
          this.message(
            "error",
            gettext("Unexpected error. Please try again or contact support.")
          );
        });
    }

    /**
     * Upload an attachment
     * @returns {Promise} A promise that resolves when the attachment is uploaded
     */
    uploadAttachment(name = null) {
      const formData = new FormData(this.uploadForm);
      if (name) {
        // override name if provided
        formData.set("name", name);
      }
      return fetch(this.options.urls.satchmo_cart_attachments, {
        method: "POST",
        body: new FormData(this.uploadForm),
      })
        .then((response) => response.json())
        .then((data) => {
          if (!data.success) {
            throw new Error(
              `An error occured when uploading the attachment: ${data.errors}`
            );
          }
        })
        .catch((error) => {
          console.log(error);
          this.message(
            "error",
            gettext("Unexpected error. Please try again or contact support.")
          );
        });
    }
  }

  /**
   * This controls the cart production modes view
   *
   */
  function CartProductionModes(element, options) {
    this.options = options;
    this.data = {};
    this.cart = options.cart;
    this.needsUpdate = true;
    this.isUpdating = false;

    this.slowModeEnabled = false;
    this.slowModeThreshold = 4000; // ms

    this.checkoutButtonBackup = "";

    // templates
    this.productionModeTemplate = document.body.querySelector(
      "#production-mode-template"
    ).innerHTML;
    this.productionModeErrorTemplate = document.body.querySelector(
      "#production-mode-error-template"
    ).innerHTML;

    // descriptions strings
    this.descriptions = {
      next_day_mode: gettext("next_day_mode"),
      fast_mode: gettext("fast_mode"),
      standard_mode: gettext("standard_mode"),
      economy_mode: gettext("economy_mode"),
    };

    // legend strings
    this.legends = {
      next_day_mode: gettext(
        "Guarantees shipment on <span>{0}</span> or your money back"
      ),
      fast_mode: gettext(
        "Guarantees shipment on <span>{0}</span> or your money back"
      ),
      standard_mode: gettext("Ships as soon as <span>{0}</span>"),
      economy_mode: gettext("Ships on <span>{0}</span>"),
    };

    this.attachDOM(element);
  }

  CartProductionModes.prototype.attachDOM = function (element) {
    this.element = element;
    this.container = this.element.querySelector("#production-modes-container");
    this.message = this.element.querySelector("#production-modes-message");
    this.about = this.element.querySelector("#production-modes-about");
    this.loadingMessage = this.element.querySelector(
      "#production-modes-loading-message"
    );
    this.loadingAnimation = this.element.querySelector(
      "#production-modes-loading-animation"
    );

    // external elements !
    // FIXME: this breaks separation of concern
    // make Cart handle this by checking ProductionModes state
    this.checkoutButton = document.querySelector(
      "#CartSummaryView #checkout-button"
    );
    this.downloadQuoteButton = document.querySelector(
      "#CartSummaryView #download-quote-button"
    );
    // In sales mode there is no checkout button, it's replaced by the download quote button
    if (!this.checkoutButton) {
      this.checkoutButton = this.downloadQuoteButton;
    }
    this.checkoutButtonBackup = this.checkoutButton.innerHTML;
  };

  CartProductionModes.prototype.render = function () {
    const simpleTemplatedElement = function (template, data) {
      let templateElem = document.createElement("template");
      let tmp = template;
      for (const [key, value] of Object.entries(data)) {
        tmp = tmp.replaceAll("##" + key + "##", value);
      }
      templateElem.innerHTML = tmp.trim();
      return templateElem.content.firstChild;
    };

    // clear the production modes container
    while (this.container.firstChild) {
      this.container.removeChild(this.container.lastChild);
    }

    // let delivery_errors = {
    //   "123456": "This is a test error message",
    //   "789012": "This is another test error message"
    // };
    // TODO: test real errors

    // render production modes
    let errors = [];
    for (const [mode_name, mode] of Object.entries(this.data)) {
      let shippingText = String.format(
        this.legends[mode_name],
        new Date(mode.delivery.min).toLocaleDateString(
          document.documentElement.lang,
          {
            year: "numeric",
            month: "long",
            day: "numeric",
          }
        )
      );
      let productionModeElement;
      if (mode.delivery.errors.length > 0) {
        productionModeElement = simpleTemplatedElement(
          this.productionModeErrorTemplate,
          {
            mode_name: mode_name,
            mode_desc: this.descriptions[mode_name],
          }
        );
        mode.delivery.errors.forEach((e) => errors.push([...e, mode_name]));
      } else {
        productionModeElement = simpleTemplatedElement(
          this.productionModeTemplate,
          {
            mode_name: mode_name,
            mode_desc: this.descriptions[mode_name],
            shipping_text: shippingText,
          }
        );
      }

      this.container.appendChild(productionModeElement);
    }
    if (this.cart) {
      this.cart.setErrors(errors);
    }
    // about link
    let showAbout =
      !this.needsUpdate &&
      ("economy_mode" in this.data || "fast_mode" in this.data);
    this.about.classList.toggle("hidden", !showAbout);

    // checkout button state
    if (this.needsUpdate) {
      this.checkoutButton.innerHTML = gettext("Update production times");
    } else {
      this.checkoutButton.innerHTML = this.checkoutButtonBackup;
    }

    // notice when needsUpdate and slowModeEnabled
    this.message.classList.toggle(
      "hidden",
      !(this.slowModeEnabled && this.needsUpdate)
    );
  };

  CartProductionModes.prototype.invalidate = function () {
    this.needsUpdate = true;
    this.data = {};
    if (this.slowModeEnabled) {
      this.render();
    } else {
      this.update();
    }
  };

  /**
   * Fetch the attached data from the server
   * @returns {Promise} A promise that resolves when the data has been fetched
   */
  CartProductionModes.prototype.update = function () {
    // show loading animation+message, hide info message and disable checkout button
    this.loadingAnimation.classList.toggle("hidden", false);
    this.loadingMessage.classList.toggle("hidden", false);
    this.message.classList.toggle("hidden", true);
    this.checkoutButton.classList.toggle("disabled", true);
    this.downloadQuoteButton.classList.toggle("disabled", true);

    let startTime = new Date();
    this.isUpdating = true;
    return fetch(this.options.urls.satchmo_cart_production_modes)
      .then((response) => response.json())
      .then((data) => {
        let timeElapsed = new Date() - startTime;
        this.slowModeEnabled = timeElapsed > this.slowModeThreshold;
        console.log(
          "production modes " +
            timeElapsed +
            "ms" +
            (this.slowModeEnabled ? " (slow)" : "")
        );

        this.data = data;
        this.needsUpdate = false;
        this.render();

        // hide loading animation+message, enable checkout button
        this.loadingAnimation.classList.toggle("hidden", true);
        this.loadingMessage.classList.toggle("hidden", true);
        this.checkoutButton.classList.toggle("disabled", false);
        this.downloadQuoteButton.classList.toggle("disabled", false);
        this.isUpdating = false;
        var $checkoutContainer = $(".checkout-next-step");
        $checkoutContainer.attr("title", "");
        if (this.cart) {
          this.cart.setUnavailableModes();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * This controls the cart attached data
   *
   */
  class CartAttachedData {
    constructor(options) {
      this.options = options;
      this.data = {};
      this.csrfToken = document.querySelector(
        "#attachment-upload-form"
      ).csrfmiddlewaretoken.value;
    }

    /**
     * Fetch the attached data from the server
     * @returns {Promise} A promise that resolves when the data has been fetched
     */
    getData() {
      return fetch(this.options.urls.satchmo_cart_attached_data)
        .then((response) => response.json())
        .then((data) => {
          this.data = data;
        })
        .catch((error) => {
          console.log(error);
        });
    }

    /**
     * Set attached data
     * @returns {Promise} A promise that resolves when the data has been set
     */
    setData(data) {
      return fetch(this.options.urls.satchmo_cart_attached_data, {
        method: "PUT",
        body: JSON.stringify(data),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-csrftoken": this.csrfToken,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.data = data;
        })
        .catch((error) => {
          console.log(error);
        });
    }

    /**
     * Patch attached data
     * @returns {Promise} A promise that resolves when the data has been patched
     */
    patchData(data) {
      return this.setData(Object.assign(this.data, data));
    }
  }

  var Cart = function (options) {
    this.options = options;
    this.refreshSelectors();

    this.preventDoubleRemoveSubmission();
    this.$el = $("body.cart");
    this.errors = [];
    this.$modalImage = $("#CartModalImageView");
    this.$cartSummaryView = $("#CartSummaryView");
    this.$accordionSalesPrices = $(".accordion-sales-prices");
    this.$salesPricesForm = $(".sales-prices-form");
    this.$accordionSalesPrices.live(
      "click",
      function (event) {
        var $button = $(event.target);
        var $itemRoot = $button.closest(".cart-cartitem-row");
        var $salesPricesForm = $(".sales-prices-form", $itemRoot);
        $salesPricesForm.toggleClass("hidden");
        $button.toggleClass("open");
      }.bind(this)
    );

    // production modes
    this.productionModes = null;
    var productionModesElement = document.querySelector(
      "#CartProductionModesView"
    );
    if (productionModesElement !== null) {
      this.productionModes = new CartProductionModes(productionModesElement, {
        urls: options.urls,
        cart: this,
      });
      this.productionModes.update();
    }

    // cart attachments
    this.cartAttachments = null;
    this.attachedData = null;
    if (this.options.is_medical) {
      this.attachedData = new CartAttachedData({
        urls: options.urls,
      });

      this.cartAttachments = new CartAttachments(
        document.querySelector("#CartAttachmentsView"),
        this.attachedData,
        {
          urls: options.urls,
          constants: options.constants,
          translations: options.translations,
        }
      );
    }

    //current language
    this.lang = $("html").attr("lang");

    this.isUpdating = false;
    this.timerQuantity;

    this.lastQuantity = $("div.forms.quantity input[name=quantity]").val();

    this.$el.on("click", ".quote-button", this.showModalQuote.bind(this));
    $(".get-a-quote-checkout-button").click(this.showModalQuote.bind(this));
    this.$el.on("click", "#change-country", this.countryChange.bind(this));
    this.$el.on(
      "click",
      ".quantity-adjustment",
      this.quantityAdjustment.bind(this)
    );
    this.$el.on("change", "[name=quantity]", this.quantityChange.bind(this));
    this.$el.on(
      "keypress",
      "[name=quantity]",
      this.quantityChangeKeyPress.bind(this)
    );

    this.$el.on(
      "click",
      ".cart-item-quantity-remove .remove",
      this.remove.bind(this)
    );
    this.$el.on(
      "click",
      "[data-form-remove-all-items-cart] a",
      this.removeAllItems.bind(this)
    );
    this.$el.on(
      "change",
      "#price-currency-selector",
      this.currencyUpdated.bind(this)
    );
    $(
      "#CartSummaryView #checkout-button, #CartSummaryView #download-quote-button"
    ).on("click", this.checkout.bind(this));

    this.$el.on(
      "change",
      "select[name=production_mode]",
      this.update_cart_production_mode.bind(this)
    );

    this.$el.on("change", "#is-test-cart", this.update_cart_is_test.bind(this));

    this.$el.on(
      "change",
      "select[name=item-usage]",
      this.update_item_usage.bind(this)
    );

    this.$el.on(
      "click",
      ".cart-item-image:not(.cart-item-image-no-link) [data-open-iframe]",
      this.renderIframeModel.bind(this)
    );
    this.$modalImage.on(
      "click",
      ".modal-background",
      this.hideImageModal.bind(this)
    );
    this.$modalImage.on(
      "click",
      ".modal-image-back",
      this.hideImageModal.bind(this)
    );

    // use for tracking google analytics
    this.$el.on(
      ".production-mode-selector select2:select",
      this.triggerGoogleTagManagerProductionMode.bind(this)
    );

    this.toggleCurrencySelector();

    this.$cartFreeShippingView = $(".CartFreeShippingView");
    if (this.$cartFreeShippingView.length) {
      // No need to check for free shipping when the cart is empty
      this.updateFreeShipping();
      this.setUnavailableModes();
    }

    this.initSelect2();
    this.modalQuote = new sculpteo.PrintModalQuote(this);

    if (this.options.cart_has_rfq_materials) {
      $(document).on(
        "change",
        'input[name="address_ship"]',
        this.checkRFQRequirements.bind(this)
      );
      setTimeout(this.checkRFQRequirements.bind(this), 500);
    }
  };

  Cart.prototype.setErrors = function (errors) {
    this.errors = errors;
  };

  Cart.prototype.setUnavailableIds = function () {
    this.errors.forEach(
      function ([itemId, errorId, mode]) {
        if (mode == "standard_mode") {
          var altName = "normal_mode";
        } else {
          var altName = mode;
        }
        var selector =
          "div.cart-cartitem-row[data-cartitem=" +
          itemId +
          "] option[data-production-mode-type=" +
          altName +
          "]";
        var $opt = $(selector);
        $opt.prop("disabled", true);
        $opt.attr(
          "title",
          gettext("This option is not available in this configuration. ")
        );
        if ($opt.prop("selected")) {
          this.hasCheckout = false;
        }
      }.bind(this)
    );
  };

  Cart.prototype.setUnavailableModes = function () {
    this.hasCheckout = true;
    this.setUnavailableIds();
    var $checkoutContainer = $(".checkout-next-step");
    var $checkoutButton = $("a", $checkoutContainer);
    if (
      this.hasCheckout &&
      this.productionModes &&
      !this.productionModes.isUpdating
    ) {
      $checkoutButton.removeClass("disabled");
      $checkoutContainer.attr("title", "");
    } else {
      $checkoutButton.addClass("disabled");
      $checkoutContainer.attr(
        "title",
        gettext(
          "Lead times cannot be automatically computed please contact the commercial team"
        )
      );
    }
  };

  Cart.prototype.showModalQuote = function (event) {
    this.modalQuote.showModal(this.getCartItems());
  };

  Cart.prototype.initSelect2 = function () {
    // activate select2
    var options = {
      minimumResultsForSearch: Infinity, // hide the search box
      width: "100%",
      dropdownParent: $(".cart-cartitem-container"),
      dropdownAutoWidth: true,
      templateSelection: this.renderSelect2Option.bind(this),
      templateResult: this.renderSelect2Option.bind(this),
    };
    var r = $(".production-mode-selector select", this.$el).select2(options);
    $("#CartSelectionView select").select2({
      minimumResultsForSearch: Infinity,
      width: "100%",
    });

    $(".item-usage select", this.$el).select2({
      minimumResultsForSearch: Infinity,
      placeholder: gettext("Please select a usage"),
    });
  };

  Cart.prototype.triggerGoogleTagManagerProductionMode = function (e) {
    // data from select2
    // data.id -> select.option.value
    var data = e.params.data;
    // get the attribute data-production-mode-type from the current option selected
    var productionMode = $(data.element).attr("data-production-mode-type");

    if (window.GoogleTagManager) {
      // send tag to TGM
      window.GoogleTagManager.sendTag({
        category: "checkout",
        action: "cart.item.detail.production",
        label: productionMode,
      });
    }
  };

  /**
   * Update the cart
   */
  Cart.prototype._update_cart = function () {
    var deferred = $.Deferred();

    $.getJSON(this.options.satchmo_cart_json).then(
      function (response) {
        this.isUpdating = false;
        response = response.body;
        // replace the whole cart container with the render of cart_base_fragment.html
        $("#CartContainer").html(response.cart_fragment);

        // we need to init select2 again as the html changed
        this.initSelect2();

        //----------------------
        //----------------------
        // This part should be remove later.
        // The logic of updating the value in javascript is no needed anymore
        // everything could be done in the template by setting directly the value
        // (the container #CartContainer is updated after each update of the cart now)

        //update free shipping "promo"
        //will be visible only if the amount is at least over 750
        this.updateFreeShipping(response.total_with_tax_no_currency);

        // replacing the content(html) of the cart breaks the delegate on the production mode selector
        // we need to refresh it
        this.refreshSelectors();

        // the DOM was completely replaced, we need to re-attach the AttachmentsView
        // controller to the new DOM
        if (this.cartAttachments) {
          this.cartAttachments.reset(
            document.querySelector("#CartAttachmentsView")
          );
        }

        $(
          "#CartSummaryView #checkout-button, #CartSummaryView #download-quote-button"
        ).on("click", this.checkout.bind(this));
        this.$cartSummaryView = $("#CartSummaryView");

        if (this.options.is_medical) {
          this.validateItemsUsage();
        }

        // reattach dom for ProductionModes
        this.productionModes.attachDOM(
          document.querySelector("#CartProductionModesView")
        );
        this.productionModes.invalidate();

        //----------------------
        //----------------------

        deferred.resolve(response.items);

        return deferred;
      }.bind(this)
    );

    return deferred;
  };

  Cart.prototype.update_cart = function (data, $form) {
    if (!data) {
      return this._update_cart();
    }
    if ("jobs" in data) {
      var deferred = $.Deferred();
      var jobs = sculpteoJob(data.jobs);
      jobs.done(
        function () {
          this.isUpdating = false;
          this.updateQuantityAjaxCall($form).done(
            function () {
              deferred.resolve();
            }.bind(this)
          );
        }.bind(this)
      );
      return deferred.promise();
    } else {
      return this._update_cart();
    }
  };

  /**
   * Update quantity
   */
  Cart.prototype.updateQuantityAjaxCall = function ($form) {
    var deferred = $.Deferred();
    var cart = this;
    var cart_item = $(
      ".cart-cartitem-row[data-cartitem='" +
        $("input[name=cartitem]", $form).val() +
        "']"
    );

    if (this.isUpdating) {
      deferred.reject();
      return deferred;
    }
    this.isUpdating = true;

    $.ajax({
      type: "POST",
      url: $form.attr("action"),
      data: $form.serialize(),
      success: function (data, textStatus, jqXHR) {
        // WIP: doing it in the front before doing it in the back
        if (
          !User.isStaff &&
          parseInt(
            data.cart_total
              .match(/[0-9]/g)
              .reverse()
              .slice(2)
              .reduce(function (s, v) {
                return v + s;
              })
          ) > 6000
        ) {
          $(".cart-cartitem-unit-price", cart_item).hide();
          $(".cart-cartitem-total-price", cart_item).hide();
          $(".cart-cartitem-get-a-quote-text", cart_item).show();
          // $(".cart-cartitem-get-a-quote", cart_item).show();
          $(".checkout-button").hide();
          $(".get-a-quote-checkout-button").show();
          // hack to reset the cartitem quantity
          $.post(
            $form.attr("action"),
            $.param(
              $form.serializeArray().map(
                function (e) {
                  if (e.name == "quantity") e.value = this.lastQuantity;
                  return e;
                }.bind(this)
              )
            ),
            function () {
              deferred.resolve();
              this.isUpdating = false;
              // $("[data-gtm-action='cart.summary.quote']").hide();
              this.$cartSummaryView.addClass("disabled");
            }.bind(this)
          );
        } else {
          // $("[data-gtm-action='cart.summary.quote']").show();
          $(".cart-cartitem-unit-price", cart_item).show();
          $(".cart-cartitem-total-price", cart_item).show();
          $(".cart-cartitem-get-a-quote-text", cart_item).hide();
          // $(".cart-cartitem-get-a-quote", cart_item).hide();
          $(".get-a-quote-checkout-button").hide();
          $(".checkout-button").show();
          this.lastQuantity = $("[name=quantity]", $form).val();
          this.update_cart(data, $form).done(function () {
            deferred.resolve();
          });
          this.$cartSummaryView.removeClass("disabled");
        }
      }.bind(this),
      error: function (data, textStatus, errorThrown) {
        var jsonError = JSON.parse(data.responseText);
        if (jsonError.errors === "Bad quantity") {
          this.update_cart(data, $form).done(function (items) {
            var id = $("[name=cartitem]", $form).val();
            var oldQty = items[parseInt(id)].qty;
            $("[name=quantity]", $form).val(oldQty);
            deferred.resolve();
          });
        }
      }.bind(this),
      // error: function(jqXHR, textStatus, errorThrown) {
      //   var data = $.parseJSON(jqXHR.responseText);
      //   Utils.popupMessage("{% trans 'Quantity change error' %}:" + data.errors, 'error');
      // },
      dataType: "json",
    });
    return deferred;
  };

  /**
   * call updateQuantityAjaxCall and trigger loading state
   */
  Cart.prototype.updateQuantity = function ($target) {
    var $row = $target.closest(".row");
    this.loadingState(true);
    this.updateQuantityAjaxCall($target.closest("form")).done(
      function () {
        this.loadingState(false);
        if (parseInt($row.find("[name=quantity]").val(), 10) < 1) {
          $row.closest(".cart-cartitem-row").remove();
        }
      }.bind(this)
    );
  };

  /**
   * callback when clicking on either plus or minus button for quantity input
   */
  Cart.prototype.quantityAdjustment = function (evt) {
    evt.preventDefault();
    var $target = $(evt.currentTarget);

    var $inputQuantity = $("input[name=quantity]", $target.parent());
    var min = parseInt($inputQuantity.attr("min"), 10) || 0;
    var max = parseInt($inputQuantity.attr("max"), 10) || 10000;
    var qty = parseInt($inputQuantity.val(), 10) || min;

    if (this.isUpdating) {
      return;
    }

    if ($target.attr("disabled")) {
      var $err = $target.parents(".quantity").find(".errorlist");
      $err.show();
      setTimeout(function () {
        $err.hide();
      }, 3000);
      return;
    }

    if ($target.hasClass("minus")) {
      // Check if the new quantity is smaller than min value
      qty = qty - 1 < min ? min : qty - 1;
    } else if ($target.hasClass("plus")) {
      // Check if the new quantity is greater than max value
      qty = qty + 1 > max ? max : qty + 1;
      $(this).toggleClass("disabled", qty == max);
    } else {
      return;
    }

    // if the quantity is above 10000. Fix it to max
    if (qty >= 10000) {
      qty = max;
    }

    // update input value
    $inputQuantity.val(qty).removeClass("isUnvalid");

    if (this.timerQuantity) {
      clearTimeout(this.timerQuantity);
      this.timerQuantity = null;
    }

    this.timerQuantity = window.setTimeout(
      function () {
        this.updateQuantity($target);
      }.bind(this),
      500
    );
  };

  /**
   * callback when pressing enter to validate new quantity
   */
  Cart.prototype.quantityChangeKeyPress = function (evt) {
    var code = evt.keyCode ? evt.keyCode : evt.which;
    if (code !== undefined && code == 13) {
      evt.preventDefault();
      // remove the focus on the element to prevent quantityChange to trigger twice
      $(evt.currentTarget).trigger("blur");
    }
  };

  /**
   * callback when changing the quantity input
   */
  Cart.prototype.quantityChange = function (evt) {
    evt.preventDefault();

    var $target = $(evt.currentTarget);

    var $inputQuantity = $("input[name=quantity]", $target.parent());
    var min = parseInt($inputQuantity.attr("min"), 10) || 0;
    var max = parseInt($inputQuantity.attr("max"), 10) || 10000;
    var qty = parseInt($inputQuantity.val(), 10) || min;

    var needUpdate = false;

    // if the quantity is not a number set it to minimum
    if (isNaN(qty)) {
      qty = min;
      needUpdate = true;
    }

    // if the quantity is above max, set it to max
    if (qty >= max) {
      qty = max;
      needUpdate = true;
    }

    if (needUpdate) {
      $inputQuantity.val(qty).removeClass("isUnvalid");
    }

    this.updateQuantity($target);
  };

  /**
   * callback when changing the country
   */
  Cart.prototype.countryChange = function (evt) {
    var $this = $(evt.currentTarget);
    $this.hide();
    var $span = $("<span/>").text("---");
    $span.insertAfter($this);
    $span.load(this.options.geoloc_country_select);
    return false;
  };

  /**
   * Renders Select2 content line.
   *
   * @param {object} data
   */
  Cart.prototype.renderSelect2Option = function (data) {
    var $el = $("<span>");
    var title = data.text;
    var $elem = $(data.element);
    var itemId = $elem.closest(".cart-cartitem-row").data("cartitem");
    var type = $elem.data("production-mode-type");
    var currency = $elem.data("production-mode-currency");
    var price = $elem.attr("data-production-mode-value");
    var format_price =
      this.lang === "en" ? price + " " + currency : currency + " " + price;

    // icons
    var $select2_icon = $("<span>").addClass(
      "icon-production-mode inline-block"
    );

    if (type === "economy_mode") {
      $select2_icon.addClass("icon-production-mode-economy_mode");
    } else if (type === "fast_mode") {
      $select2_icon.addClass("icon-production-mode-fast_mode");
    } else if (type === "next_day_mode") {
      $select2_icon.addClass("icon-production-mode-next_day_mode");
    } else {
      $select2_icon.addClass("icon-production-mode-standard");
    }

    var $select2_title = $("<span>")
      .addClass("select2-selection__title")
      .html(title);
    var $select2_price = $("<span>")
      .addClass("select2-selection__price")
      .html(format_price);

    $el.append($select2_icon);
    $el.append($select2_title);
    $el.append($select2_price);

    return $el;
  };

  Cart.prototype.renderIframeModel = function (e) {
    //add a nice loading circle until the iframe is loaded
    $(".intrinsic-container", this.$modalImage).addClass("loading-circle");

    var $design_data = $(e.currentTarget)
      .closest("[data-item-on-cart]")
      .find(".cart-thumbnail");

    var $design_slug = $design_data.data("design-slug");
    var $design_uuid = $design_data.data("design-uuid");
    var $design_model_uuid = $design_data.data("design-model-uuid");

    //set the url based on the current choose design
    var url = "/print_iframe/";
    url += $design_slug;
    url += "/";
    url += $design_uuid;
    url += "?uuid=" + $design_model_uuid;
    url += "&soliditycheck=true";

    //solidity check or not
    if (this.typeOfImage == "solidity") {
      url += "&soliditycheck=true";
    }

    //set the iframe src
    $(".iframe-render-model", this.$modalImage).prop("src", url);

    //when it is loaded removed the loading circle
    $(".iframe-render-model", this.$modalImage).load(
      function () {
        $(".intrinsic-container", this.$modalImage).removeClass(
          "loading-circle"
        );
      }.bind(this)
    );

    this.$modalImage.show();

    if (window.GoogleTagManager) {
      // send tag to TGM
      window.GoogleTagManager.sendTag({
        category: "checkout",
        action: "cart.item.detail.viewer",
        label: "solidity",
      });
    }
  };

  Cart.prototype.hideImageModal = function () {
    this.$modalImage.hide();
    $(".iframe-render-model", this.$modalImage).prop("src", "");
  };

  Cart.prototype.remove = function (event) {
    event.preventDefault();

    // loading state
    this.loadingState(true);

    var $form = $(event.currentTarget).parents("form");
    $.post($form.attr("action"), $form.serialize()).always(
      function () {
        $form.closest(".cart-cartitem-row").remove();

        // Update the cart shopping badge items number
        var rowItem = $form
          .closest(".cart-cartitem-row")
          .children()
          .children()[0];
        var itemIsDisabled = $(rowItem).hasClass("disabled");
        if (!itemIsDisabled) {
          var cartNbItems = $(".nav-shopping-badge", $("#header"));
          var nbItems = cartNbItems.text();
          cartNbItems.text(nbItems - 1);
        }

        //after the update. We get a list of current item in cart
        //check and delete item that are on the html cart page and not in the list of the item in the cart
        //used for dyemansion
        this.update_cart().done(
          function (itemInCart) {
            var cart_item_raw = $(".cart-cartitem-row");
            var needReload = false;
            cart_item_raw.each(function (index) {
              if (
                Object.keys(itemInCart).indexOf(
                  $(this).data("cartitem").toString()
                ) == -1
              ) {
                $(this).remove();
              }
              // use for dyemansion to fix the dyemansion price correctly in some cases
              // need reload will be true on the dyemansion package
              if ($(this).data("need-reload")) {
                needReload = true;
              }
            });
            if (needReload) {
              window.location.reload();
            } else {
              // only unset loading state if there is no need to reload the page
              // dyemansion....
              this.loadingState(false);
              this.$cartSummaryView.removeClass("disabled"); //Quick fix
              this.toggleCurrencySelector();
            }
          }.bind(this)
        );
      }.bind(this)
    );
  };

  Cart.prototype.removeAllItems = function (event) {
    event.preventDefault();

    var $form = $(event.currentTarget).parents("form");

    $.post($form.attr("action"), $form.serialize()).always(
      function () {
        window.location.reload();
      }.bind(this)
    );
    this.toggleCurrencySelector();
  };

  Cart.prototype.checkout = function (event) {
    event.preventDefault();
    var $button = $(event.currentTarget);

    // prevent continuing if cart attachments are not valid
    // this will also display the error message in the attachments view
    if (this.options.is_medical && !this.cartAttachments.validate()) {
      return;
    }

    if (!this.validateItemsUsage()) {
      return;
    }

    // prevent continuing and update production modes if needed
    if (this.productionModes && this.productionModes.needsUpdate) {
      this.productionModes.update();
      return;
    }

    if (this.$cartSummaryView.hasClass("disabled")) {
      return;
    }

    var setLoadingAndDisableButton = function () {
      this.loadingState(true);
      $button.addClass("loading").addClass("disabled");
    }.bind(this);

    // show login/signup modal if user is not logged in
    if (!User.isLoggedIn) {
      window.baseController.views.loginSignup
        .showSignUp(false, window.location.toString())
        .done(
          function () {
            if (!User.isLoggedIn) {
              // user canceled/failed to log-in/signup. Cannot checkout
              return;
            }
            setLoadingAndDisableButton();
            window.location = $button.attr("href");
            // we need to explicitely "click" since we called preventDefault above
          }.bind(this)
        );
    } else {
      setLoadingAndDisableButton();
      window.location = $button.attr("href");
    }
  };

  Cart.prototype.validateItemsUsage = function () {
    var $items = $(".cart-cartitem-row");
    var isValid = true;

    $items.each(function () {
      var $item = $(this);
      var $usageSelect = $item.find("select[name=item-usage]");
      var $sculpteoSelect2 = $item.find(".item-usage .sculpteo-select2");
      var $sculpteoSelect2error = $item.find(
        ".item-usage .sculpteo-select2-error"
      );

      if ($usageSelect.length > 0 && $usageSelect.val() == "") {
        isValid = false;
        $sculpteoSelect2.addClass("has-error");
        $sculpteoSelect2error.removeClass("hidden");
      } else {
        $sculpteoSelect2.removeClass("has-error");
        $sculpteoSelect2error.addClass("hidden");
      }
    });

    var $cartItemsMessage = $("#cart-items-message");
    $cartItemsMessage.toggleClass("hidden", isValid);

    return isValid;
  };

  Cart.prototype.loadingState = function (state) {
    if (state) {
      $(".card", this.$el).addClass("disabled");
    } else {
      $(".card", this.$el).removeClass("disabled");
    }
  };

  Cart.prototype.currencyUpdated = function (event) {
    var $target = $(event.currentTarget);
    var currency = $target.val();
    var symbol = $("option:selected", $target).text();

    this.loadingState(true);

    if (window.GoogleTagManager) {
      // send tag to TGM
      window.GoogleTagManager.sendTag({
        category: "checkout",
        action: "cart.summary.currency",
        label: currency,
      });
    }

    /*
    we can do it better than reloading the page
    BUT the code that handle the production mode selector on each items is a template tag
    I don't want to duplicate the code to make it possible to call it from javascript.
    We might be able to redraw the dom with the correct value if we split the template of the production mode
      Something like: production_mode_select.html
    At the moment, reload the page will do....
    */
    return $.getJSON(URLS["set_currency"] + currency).success(
      function (data) {
        // update_cart().done(function(itemInCart){
        //   this.loadingState(false);
        // }.bind(this));
        // this.options.currency = symbol;
        // this.updateFreeShippingCurrency();
        location.reload();
      }.bind(this)
    );
  };

  Cart.prototype.preventDoubleRemoveSubmission = function () {
    $("[data-form-remove-from-cart]").submit(function () {
      $(this).submit(function () {
        return false;
      });
      return true;
    });
  };

  Cart.prototype.update_item_usage = function (event) {
    var itemId = $(event.currentTarget).data("cartitem");
    var usage = $(event.currentTarget).val();

    $.ajax({
      type: "POST",
      url: this.options.urls.update_cart_item_usage,
      data: { itemId: itemId, usage: usage },
      dataType: "json",
      success: function (response) {
        this.update_cart().done(
          function () {
            this.loadingState(false);
            this.toggleCurrencySelector();
          }.bind(this)
        );
      }.bind(this),
    });
  };

  Cart.prototype.update_cart_production_mode = function () {
    var data = {};

    this.loadingState(true);

    this.$select.each(function (i, el) {
      var $el = $(el);
      var icon = $el.parents(".row").find(".icon-production-mode");

      data[$el.data("cartitem")] = $el.val();
    });

    $.ajax({
      type: "POST",
      url: this.options.update_mode_url,
      data: data,
      dataType: "json",
      success: function (response) {
        //global method
        //update all the price on the page
        //TODO will be migrate on that file
        this.update_cart().done(
          function () {
            this.loadingState(false);
            this.toggleCurrencySelector();
          }.bind(this)
        );
      }.bind(this),
    });
  };

  Cart.prototype.update_cart_is_test = function (event) {
    var enabled = $(event.currentTarget).is(":checked") ? 1 : 0;

    this.loadingState(true);

    $.ajax({
      type: "POST",
      url: this.options.urls.update_cart_test,
      data: { is_test: enabled },
      dataType: "json",
      success: function (response) {
        this.update_cart().done(
          function () {
            this.loadingState(false);
            this.toggleCurrencySelector();
          }.bind(this)
        );
      }.bind(this),
    }).always(
      function () {
        window.location.reload();
      }.bind(this)
    );
  };

  Cart.prototype.refreshSelectors = function () {
    this.$select = $("select[name=production_mode]:visible");
    this.$checkboxes = $(".production-mode-switcher input[type=checkbox]");
  };

  //TODO: Improve the code of updateFreeShipping
  /**
   * Update the free shipping
   *
   * @param {cart_total} string
   */
  Cart.prototype.updateFreeShipping = function (cart_total) {
    var brEntryDate = document
      .getElementById("user-info")
      .getAttribute("data-br-entry-date");

    if (brEntryDate) {
      var entryDate = new Date(brEntryDate);
      var cutoffDate = new Date("2024-12-17");

      // Asia countries excluded from BR free shipping
      var asiaCountries = [
        "CN",
        "JP",
        "KR",
        "SG",
        "HK",
        "TW",
        "TH",
        "MY",
        "ID",
        "IN",
        "VN",
        "PH",
        "BD",
        "PK",
        "KH",
        "LA",
        "MM",
        "MN",
        "NP",
        "LK",
      ];
      var isAsiaCountry =
        asiaCountries.indexOf(this.options.ship_country) !== -1;

      if (
        !isAsiaCountry &&
        (entryDate <= cutoffDate ||
          this.options.business_rewards_status === "Platinum" ||
          this.options.business_rewards_status === "Diamond" ||
          this.options.business_rewards_status === "Elite")
      ) {
        this.options.freeShipping_threshold_visible = 0;
        this.options.freeShipping_threshold =
          this.options.freeShipping_threshold_business_rewards;
      }
    }

    $(".free_shipping_threshold").html(this.options.freeShipping_threshold);

    if (!cart_total) {
      cart_total = $(".promo-slide-state", this.$cartFreeShippingView).data(
        "start-up-cart-total"
      );
    }

    cart_total = parseFloat(cart_total);

    this.$cartFreeShippingView = $(".CartFreeShippingView");

    if (cart_total < this.options.freeShipping_threshold_visible) {
      //hide the div if the total is not over freeShipping_threshold_visible
      this.$cartFreeShippingView.hide();
      //don't update the rest if it is not visible
      return;
    } else {
      this.$cartFreeShippingView.show();
      this.updateFreeShippingCurrency();
    }

    var percentage =
      ((cart_total - this.options.freeShipping_threshold_visible) /
        (this.options.freeShipping_threshold -
          this.options.freeShipping_threshold_visible)) *
      100;
    percentage = percentage.toFixed(2);

    if (percentage >= 100) {
      percentage = 100;
    }

    $(".promo-slide-state", this.$cartFreeShippingView).css(
      "width",
      percentage + "%"
    );

    var dif = this.options.freeShipping_threshold - cart_total;
    var message = gettext(
      "Add <span class='text--bold'>XXXX</span> to qualify for free shipping"
    ).replace("XXXX", dif.toFixed(2) + this.options.currency);

    if (cart_total < this.options.freeShipping_threshold) {
      $(".promo-slide", this.$cartFreeShippingView).show();
      $(".promo-message-text-ongoing", this.$cartFreeShippingView).show();
      $("span[data-summary-delivery]")
        .removeClass("text--bold text--blue")
        .html("---");
    } else {
      $(".promo-slide", this.$cartFreeShippingView).hide();
      $(".promo-message-text-ongoing", this.$cartFreeShippingView).hide();

      message = gettext(
        "<span class='text--bold text--xl'>You qualify for free shipping!</span>"
      );
      $("span[data-summary-delivery]")
        .addClass("text--bold text--blue")
        .html(gettext("FREE"));
    }

    $(".promo-slide-message", this.$cartFreeShippingView).html(message);
  };

  Cart.prototype.updateFreeShippingCurrency = function () {
    $(".js-currency", this.$cartFreeShippingView).html(this.options.currency);
  };

  Cart.prototype.toggleCurrencySelector = function () {
    var $productionMode = $("select[name='production_mode'] option:selected");
    $("#price-currency-selector").removeAttr("disabled");
    $("#CartSummaryView .tooltiptext").removeClass("active");
    var items = [];
    if ($productionMode.length > 0) {
      for (var i = 0; i < $productionMode.length; i++) {
        var mode = $productionMode[i].value;
        var $itemRow = $($productionMode[i])
          .parent()
          .parent()
          .parent()
          .parent()
          .parent()
          .parent();
        var item = $("h4 a", $itemRow);
        if (mode == "fast_mode" || mode == "next_day_mode") {
          items.push(item.text());
          $("#price-currency-selector").attr("disabled", "disabled");
          $("#CartSummaryView .tooltiptext").addClass("active");
        }
      }
      if (items) {
        $(".european_items").text(items.join(", "));
      }
    }
  };

  Cart.prototype.getCartItems = function () {
    var cartitems = [];
    $(".cart-cartitem-row").each(function (i, cartitem) {
      // TODO: handle promotional items
      var material = $(cartitem)
        .find(".cart-item-information p:nth-child(3)")
        .text();
      if (material)
        cartitems.push({
          quantity: $(cartitem).find("input[type=number]").val(),
          url: $(cartitem).find(".cart-item-information h4 a").attr("href"),
          material: material.split(":")[1].trim(),
        });
    });
    return cartitems;
  };

  window.Cart = Cart;
})();
